import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiFetch, isApiError } from "@/api/client";
import { ep } from "@/api/endpoints";
import { qk } from "@/api/queryKeys";
import type { Conversation, ConversationPatchRequest, ConversationPatchResponse, ModelId, SettingsPatchRequest, SettingsResponse } from "@/api/types";
import { t } from "@/i18n/ko";
import { useAuthStore } from "@/stores/authStore";
import { useConfigStore } from "@/stores/configStore";
import { useUiStore } from "@/stores/uiStore";
import { usePlot } from "@/features/plot/usePlot";

/**
 * 1.10 모델 변경·프리셋 숨김.
 * - setConversationModel: qk.conversation.model 낙관 패치 → PATCH /api/conversations/:id {model} 로 그 대화 last_model 에 바로 저장, 실패 롤백(AC0-231). users.default_model 은 그대로.
 * - setHidePresets: PATCH /api/me/settings {hidePresets} → 성공 전 qk.conversation.presets를 null/플롯 presets로 낙관 패치, 실패 롤백.
 * - setDefaultModel: PATCH /api/me/settings {defaultModel}(새 대화 기본, 설정 화면용).
 */
export function useChatSettings(plotKey: string, plotId: string) {
  const qc = useQueryClient();
  const plot = usePlot(plotKey); // 주소의 slug(또는 예전 UUID) — ChatPage 와 같은 캐시. 대화 캐시 키는 plot.id (AC0-161)
  const showSnackbar = useUiStore((s) => s.showSnackbar);
  const key = qk.conversation(plotId);

  const patchSettings = async (body: SettingsPatchRequest) => {
    const res = await apiFetch<SettingsResponse>(ep.meSettings, { method: "PATCH", body });
    useAuthStore.setState({ settings: res.settings });
    return res.settings;
  };

  const hidePresets = useMutation({
    mutationFn: (hide: boolean) => patchSettings({ hidePresets: hide }),
    onMutate: async (hide) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<Conversation>(key);
      qc.setQueryData<Conversation>(key, (old) => (old ? { ...old, presets: hide ? null : (plot.data?.presets ?? old.presets ?? []) } : old));
      const prevSettings = useAuthStore.getState().settings;
      if (prevSettings) useAuthStore.setState({ settings: { ...prevSettings, hidePresets: hide } });
      return { prev, prevSettings };
    },
    onError: (e, _hide, ctx) => {
      if (ctx?.prev) qc.setQueryData(key, ctx.prev);
      if (ctx?.prevSettings) useAuthStore.setState({ settings: ctx.prevSettings });
      showSnackbar(isApiError(e) && e.code === "NETWORK" ? t("error.network") : t("chat.settings.failed"));
    },
  });

  const defaultModel = useMutation({
    mutationFn: (model: ModelId) => patchSettings({ defaultModel: model }),
    onError: (e) => {
      if (isApiError(e) && e.code === "MODEL_UNAVAILABLE") {
        showSnackbar(t("turn.modelUnavailable"));
        return;
      }
      showSnackbar(isApiError(e) && e.code === "NETWORK" ? t("error.network") : t("chat.settings.failed"));
    },
  });

  const conversationModel = useMutation({
    mutationFn: ({ conversationId, model }: { conversationId: string; model: ModelId }) =>
      apiFetch<ConversationPatchResponse>(ep.conversation(conversationId), { method: "PATCH", body: { model } satisfies ConversationPatchRequest }),
    onMutate: ({ model }) => {
      const prevModel = qc.getQueryData<Conversation>(key)?.model;
      qc.setQueryData<Conversation>(key, (old) => (old ? { ...old, model } : old));
      return { prevModel };
    },
    onError: (e, { model }, ctx) => {
      // 그사이 다른 모델을 또 골랐으면 그 선택을 덮지 않는다
      const prevModel = ctx?.prevModel;
      if (prevModel) qc.setQueryData<Conversation>(key, (old) => (old && old.model === model ? { ...old, model: prevModel } : old));
      if (isApiError(e) && e.code === "MODEL_UNAVAILABLE") {
        showSnackbar(t("chat.model.unavailable"));
        return;
      }
      showSnackbar(isApiError(e) && e.code === "NETWORK" ? t("error.network") : t("chat.model.saveFailed"));
    },
  });

  const setConversationModel = (model: ModelId) => {
    const known = useConfigStore.getState().settings?.pricing.models.some((m) => m.id === model);
    if (!known) return;
    const conv = qc.getQueryData<Conversation>(key);
    if (!conv || conv.model === model) return;
    conversationModel.mutate({ conversationId: conv.id, model });
  };

  return {
    setConversationModel,
    setHidePresets: (hide: boolean) => hidePresets.mutate(hide),
    setDefaultModel: (model: ModelId) => defaultModel.mutate(model),
    pending: hidePresets.isPending || defaultModel.isPending,
  };
}
